<template>
  <div
    class="notification"
    :class="{
      'notification--aberta': dropdownEstaVisivel,
      'notification--controlada-externamente': controladoExternamente
    }"
  >
    <a
      v-if="!controladoExternamente"
      class="to-click main-header__button--category dropdown-toggle"
      @click.prevent="abrirGavetaDeRelatorios"
      type="primary"
      @keyup.enter="abrirGavetaDeRelatorios"
      aria-label="Notificações"
      aria-haspopup="dialog"
      tabindex="0"
    >
      <div class="icone-chapado-sino"></div>
      <span v-if="notificacoesNaoLidas !== 0" class="badge">{{notificacoesNaoLidas}}</span>
      <span v-if="notificacoesNaoLidas === 0 && !relatoriosDeTodasAsFilasEstaoProntos" class="badge">
        <div class="icone-linha-atualizar icon-spin"></div>
      </span>
      <div class="arrow-img icone-chapado-seta-baixo"></div>
    </a>

    <pontotel-menu-vertical-gaveta-lateral
      v-if="dropdownEstaVisivel"
      :titulo="$t('mainHeader.Notification.tituloGavetaRelatorios')"
      :margem="margem"
      largura="220px"
      @fechar="fecharGavetaDeRelatorios"
    >
      <div class="gaveta">
        <div class="gaveta__secao">
          <p class="secao__titulo texto-titulo-p">{{ $t('mainHeader.Notification.secaoEmAndamento') }}</p>

          <p
            v-if="!totalDeRelatoriosEmAndamentoDaGaveta"
            class="secao__mensagem-vazia texto-paragrafo-pp"
          >
            {{ $t('mainHeader.Notification.semRelatoriosEmAndamento') }}
          </p>

          <div
            v-for="relatorio in relatoriosEmAndamentoDaGaveta"
            :key="relatorio.chave"
            class="relatorio relatorio--em-andamento"
            :class="{
              'relatorio--pode-cancelar': relatorio.podeCancelar,
              'relatorio--nao-pode-cancelar': !relatorio.podeCancelar
            }"
          >
            <pontotel-tooltip
              v-if="!relatorio.podeCancelar"
              class="relatorio__tooltip-indisponivel"
              posicao="direita"
              tipo="generico"
              modo="hover"
              :texto="$t('mainHeader.Notification.tooltipRelatorioNaoPodeSerCancelado')"
            >
              <div class="relatorio__cabecalho">
                <span class="relatorio__nome texto-paragrafo-pp">{{ relatorio.nome }}</span>
                <p v-if="relatorio.progresso" class="relatorio__progresso texto-paragrafo-pp">{{ relatorio.progresso }}</p>
              </div>
            </pontotel-tooltip>

            <div
              v-else
              class="relatorio__cabecalho"
            >
              <span class="relatorio__nome texto-paragrafo-pp">{{ relatorio.nome }}</span>
              <p v-if="relatorio.progresso" class="relatorio__progresso texto-paragrafo-pp">{{ relatorio.progresso }}</p>
            </div>
            <pontotel-tooltip
              v-if="relatorio.podeCancelar"
              class="relatorio__acao-tooltip relatorio__acao-tooltip--cancelar"
              posicao="direita"
              tipo="generico"
              modo="hover"
              :texto="$t('mainHeader.Notification.tooltipCancelarRelatorio')"
            >
              <pontotel-botao
                class="relatorio__acao relatorio__acao--cancelar"
                peso="intermediario"
                tipo="erro"
                tamanho="P"
                redondo
                :aria-label="$t('mainHeader.Notification.cancelarRelatorio')"
                @click.stop.prevent="cancelarRelatorioDaGaveta(relatorio)"
              >
                <IconeSVG
                  icone="erro_linha_sem_borda"
                  width="10px"
                  height="10px"
                />
              </pontotel-botao>
            </pontotel-tooltip>
          </div>
        </div>

        <div class="gaveta__secao gaveta__secao--sucesso">
          <p class="secao__titulo texto-titulo-p">{{ $t('mainHeader.Notification.secaoConcluido') }}</p>

          <p
            v-if="!totalDeRelatoriosConcluidosDaGaveta"
            class="secao__mensagem-vazia texto-paragrafo-pp"
          >
            {{ $t('mainHeader.Notification.semRelatoriosConcluidos') }}
          </p>

          <div
            v-for="relatorio in relatoriosConcluidosDaGaveta"
            :key="relatorio.chave"
            class="relatorio relatorio--concluido"
          >
            <span class="relatorio__nome texto-paragrafo-pp">{{ relatorio.nome }}</span>
            <pontotel-tooltip
              class="relatorio__acao-tooltip relatorio__acao-tooltip--download"
              posicao="direita"
              tipo="generico"
              modo="hover"
              :texto="$t('mainHeader.Notification.tooltipBaixarRelatorio')"
            >
              <pontotel-botao
                class="relatorio__acao relatorio__acao--download"
                peso="intermediario"
                tipo="secundario"
                tamanho="P"
                redondo
                :aria-label="$t('mainHeader.Notification.baixarRelatorio', { nome: relatorio.nome })"
                @click.stop.prevent="abrirRelatorioDaGaveta(relatorio)"
              >
                <IconeSVG
                  icone="download"
                  width="10px"
                  height="10px"
                />
              </pontotel-botao>
            </pontotel-tooltip>
          </div>
        </div>
      </div>
    </pontotel-menu-vertical-gaveta-lateral>
  </div>
</template>

<script>
import { differenceBy } from 'lodash'
import { mapState } from 'vuex'
import moment from 'moment'
import { CanceledError } from 'axios';
import { offCheckUserFiles, onCheckUserFiles } from 'src/events/notifications'

export default {
  inject: ['prepararObservacaoImportacoes'],
  /**
     * Permite que um componente pai controle a exibição desta gaveta
     * Quando a flag `controladoExternamente` estiver ativa,
     * o gatilho de sino padrão é ocultado e o componente pai se torna responsável por:
     *
     * 1. Controlar a visibilidade: deve utilizar ref para chamar `alterarVisibilidadeDropdown()`
     * para abrir/fechar a gaveta.
    */
  props: {
    controladoExternamente: {
      type: Boolean,
      default: false,
    },
    margem: {
      type: String,
      default: '0 0 0 65px',
    }
  },
  data () {
    return {
      dropdownEstaVisivel: false,
      checkUserFilesHandler: null,
      controladorRequisicao: new AbortController(),
      instanciaTimeoutCelery: null,
      relatorioInfo: {},
      relatorioLista: {},
      filaDeRelatoriosAntigos: [],
      notificacoesNaoLidas: 0,
      timer: null,
      primeiraVezQueConsultaFilaAntiga: true,
      estaBuscandoFilas: false,
      showActivateNotification: true,
      primeiraVezQueConsulta: true,
      relatorioFilaTravamentoEstaPronto: false,
      relatorioFilaAntigaEstaPronto: false,
      relatorioNovaFilaEstaPronto: false,
      relatorioAcompanhamentoIntegracaoEstaPronto: false,
      filaDeRelatorios: [],
      relatoriosConcluidos: [],
      relatoriosEmAndamento: [],
      relatoriosLidos: [],
      errosEInfosJaEnviadas: [],
      totalDeNovosRelatoriosEmAndamento: 0,
      relatoriosAntigosConcluidos: [],
      relatoriosAntigosEmAndamento: [],
      totalDeRelatoriosAntigosEmAndamento: 0,
      primeiraVezConsultaFilaTravamento: true,
      filaDeRelatoriosTravamento: [],
      relatoriosFilaTravamentoEmEndamento: [],
      relatorioFilaTravamentoConcluidos: [],
      totalDeRelatoriosFilaTravamentoEmEndamento: 0,
      primeiraVezQueConsultaAcompanhamentoDeIntegracao: true,
      filaDeRelatoriosAcompanhamentoDeIntegracao: [],
      relatoriosFilaAcompanhamentoDeIntegracaoEmEndamento: [],
      relatorioFilaAcompanhamentoDeIntegracaoConcluidos: [],
      totalDeRelatoriosFilaAcompanhamentoDeIntegracaoEmEndamento: 0
    }
  },
  computed: {
    ...mapState('notification', ['reportLoading', 'lastNotification']),
    notificationDisabled () {
      return Notification.permission !== 'granted'
    },
    listaDeRelatoriosConcluidos () {
      return [...this.relatoriosConcluidos, ...this.relatoriosAntigosConcluidos, ...this.relatorioFilaTravamentoConcluidos, ...this.relatorioFilaAcompanhamentoDeIntegracaoConcluidos]
    },
    totalDeRelatoriosConcluidos () {
      return Number(this.listaDeRelatoriosConcluidos.length)
    },
    relatoriosConcluidosDaGaveta () {
      return this.listaDeRelatoriosConcluidos
        .filter(relatorio => relatorio.url)
        .map((relatorio, index) => ({
          chave: `concluido-${relatorio.id || relatorio.url || index}`,
          nome: this.obterNomeDoRelatorio(relatorio),
          url: relatorio.url
        }))
    },
    totalDeRelatoriosConcluidosDaGaveta () {
      return this.relatoriosConcluidosDaGaveta.length
    },
    listaDeRelatoriosEmAndamento () {
      return [...this.relatoriosEmAndamento, ...this.relatoriosAntigosEmAndamento, ...this.relatoriosFilaTravamentoEmEndamento, ...this.relatoriosFilaAcompanhamentoDeIntegracaoEmEndamento]
    },
    totalDeRelatoriosEmAndamento () {
      return Number(this.listaDeRelatoriosEmAndamento.length)
    },
    relatoriosCeleryEmAndamento () {
      return Object.keys(this.relatorioLista || {}).map(taskId => {
        const relatorio = this.relatorioLista[taskId] || {}

        return {
          chave: `celery-${taskId}`,
          id: relatorio.id || taskId,
          nome: this.obterNomeDoRelatorio(relatorio),
          progresso: this.obterDescricaoDeProgressoRelatorio(relatorio),
          podeCancelar: false,
          itemOriginal: relatorio
        }
      })
    },
    relatoriosEmAndamentoDaGaveta () {
      const mapearRelatoriosDaFila = (relatorios, origem) => relatorios.map((relatorio, index) => ({
        chave: `fila-${origem}-${relatorio.id || relatorio.name || index}`,
        id: relatorio.id,
        nome: this.obterNomeDoRelatorio(relatorio),
        progresso: origem === 'antiga' ? '' : this.obterDescricaoDeProgressoRelatorio(relatorio),
        podeCancelar: this.verificarSeRelatorioPodeSerCancelado(relatorio),
        itemOriginal: relatorio
      }))

      const relatoriosDasFilas = [
        ...mapearRelatoriosDaFila(this.relatoriosEmAndamento, 'nova'),
        ...mapearRelatoriosDaFila(this.relatoriosAntigosEmAndamento, 'antiga'),
        ...mapearRelatoriosDaFila(this.relatoriosFilaTravamentoEmEndamento, 'travamento'),
        ...mapearRelatoriosDaFila(this.relatoriosFilaAcompanhamentoDeIntegracaoEmEndamento, 'acompanhamento-integracao')
      ]

      return [...this.relatoriosCeleryEmAndamento, ...relatoriosDasFilas]
    },
    totalDeRelatoriosEmAndamentoDaGaveta () {
      return this.relatoriosEmAndamentoDaGaveta.length
    },
    quantidadeRelatoriosEmAndamento () {
      if (!this.totalDeRelatoriosEmAndamentoDaGaveta) {
        return null
      }

      return this.totalDeRelatoriosEmAndamentoDaGaveta
    },
    possuiPermissaoDeVerRelatorio () {
      return this.$hasPermission({ module: 'exportfiles', action: 'get' })
    },
    relatoriosDeTodasAsFilasEstaoProntos () {
      return this.relatorioFilaTravamentoEstaPronto &&
      this.relatorioFilaAntigaEstaPronto &&
      this.relatorioNovaFilaEstaPronto &&
      this.relatorioAcompanhamentoIntegracaoEstaPronto
    },
    possuiFlagIntegracaoAtiva () {
      return this.$store.state.userInfo.compMan.possuiIntegracaoAutomatica
    },
    modalEstaAberto () {
      return this.dropdownEstaVisivel;
    },
    intervaloDeTimeout () {
      // se o sininho estiver aberto, o intervalo de polling é de 5 segundo
      // se não, o intervalo é de 20 segundos
      const modalEstaAberto = this.modalEstaAberto
      const intervaloEmMilissegundos = modalEstaAberto ? 5000 : 20000

      return intervaloEmMilissegundos
    },
    chaveLocalStorage() {
      const userId = this.$store.state.userInfo.id;
      const compManId = this.$store.state.userInfo.compMan.id;

      return `relatoriosPendentes-${compManId}-${userId}`
    }
  },
  created () {
    this.checkUserFilesHandler = (relatorioInfo) => this.buscarNotificacoes(relatorioInfo)

    const relatorioListaLocalStorage = localStorage.getItem(this.chaveLocalStorage);

    if (relatorioListaLocalStorage && Object.keys(relatorioListaLocalStorage).length) {
      this.relatorioLista = JSON.parse(relatorioListaLocalStorage);
      this.extrairInformacoesEProcessarRelatorios(this.relatorioLista)
    }
  },
  mounted () {
    this.polling()
    onCheckUserFiles(this.checkUserFilesHandler)
  },
  methods: {
    extrairInformacoesEProcessarRelatorios(relatorioLista){
      Object.keys(relatorioLista).forEach(taskId => {
        const relatorioInfo = relatorioLista[taskId];
        this.pollingCelery(relatorioInfo)
      });
    },
    removerChaveVaziaRelatoriosPendentes(){
      const relatorioListaLocalStorage = localStorage.getItem(this.chaveLocalStorage);

      if (relatorioListaLocalStorage) {
        const relatorioLista = JSON.parse(relatorioListaLocalStorage);

        if (Object.keys(relatorioLista).length === 0) {
          localStorage.removeItem(this.chaveLocalStorage);
        }
      }
    },
    abrirGavetaDeRelatorios () {
      this.buscarNotificacoes()
      this.dropdownEstaVisivel = true
    },
    fecharGavetaDeRelatorios () {
      this.dropdownEstaVisivel = false
      this.$emit('fechar')
    },
    obterNomeDoRelatorio (relatorio) {
      return relatorio?.name || relatorio?.tipo || this.$t('mainHeader.Notification.nomeRelatorioPadrao')
    },
    obterDescricaoDeProgressoRelatorio (relatorio) {
      if (relatorio?.status === 'NAO_INICIADO') {
        return this.$t('mainHeader.Notification.iniciaraEmBreve')
      }

      return this.gerarInformacoesDoRelatorio(relatorio?.percent, relatorio?.ttf, relatorio?.status).replace(/^-\s*/, '')
    },
    cancelarRelatorioDaGaveta (relatorio) {
      if (!relatorio.podeCancelar) return

      return this.cancelarRelatorio(relatorio.id)
    },
    abrirRelatorioDaGaveta (relatorio) {
      const novaJanela = window.open(relatorio.url, '_blank', 'noopener,noreferrer')

      if (novaJanela) {
        novaJanela.opener = null
      }
    },
    verificarSeRelatorioPodeSerCancelado (relatorio) {
      return relatorio.id && ['NAO_INICIADO', 'GERANDO'].includes(relatorio.status)
    },
    gerarInformacoesDoRelatorio(porcentagem, tempoRestante, status) {
      if (status === 'NAO_INICIADO') {
        return this.$t('mainHeader.Notification.avisoIniciaraEmBreve')
      }

      if (!isNaN(tempoRestante)) {
        return ''
      }

      if (typeof tempoRestante !== 'string' || !tempoRestante.includes(':')) {
        return ''
      }

      porcentagem = porcentagem / 10
      if (porcentagem > 100 || porcentagem < 0) {
        return ''
      }

      const [h, m] = tempoRestante.split(':')
      const horas = Number(h)
      const minutos = Number(m)

      if (horas > 0) {
        return this.$t('mainHeader.Notification.porcentagemHorasRestantes', {
          porcentagem,
          horas,
          minutos
        })
      } else if (minutos > 0) {
        return this.$t('mainHeader.Notification.porcentagemMinutosRestantes', {
          porcentagem,
          minutos
        })
      } else {
        return this.$t('mainHeader.Notification.porcentagemRestantes', {
          porcentagem
        })
      }
    },
    async cleanQueueReports () {
      const { compMan, id } = this.$store.state.userInfo

      try {
        await this.$API.clearUserFile.get({ compManId: compMan.id, userId: id })

        let relatoriosDaNovaFilaParaCancelar = this.obterIdsDeRelatoriosQueEstaoSendoGerados()

        if (relatoriosDaNovaFilaParaCancelar.length > 0) {
          await this.$API.cancelarGeracaoDeRelatorio.save({ ids_relatorios: relatoriosDaNovaFilaParaCancelar })

          this.$swal({
            type: 'success',
            title: this.$t('mainHeader.Notification.avisoFilaLimpa')
          })
        }
      } catch (erro) {
        this.$filtradorDeErros.capturarErro(erro)
      }
    },
    activateBrowserNotification () {
      Notification.requestPermission()
      this.showActivateNotification = false
    },
    async polling () {
      if (!this.estaBuscandoFilas && (!this.relatoriosDeTodasAsFilasEstaoProntos || this.totalDeRelatoriosEmAndamento > 0)) {
        this.estaBuscandoFilas = true
        const resultado = await Promise.allSettled([
          this.buscarFilaDeRelatoriosAntiga(),
          this.buscarFilaDeRelatorioTravamento(),
          this.buscarNovaFilaDeRelatorios(),
          this.buscarFilaDeAcompanhamentoIntegracao()
        ])
        this.estaBuscandoFilas = false

        this.lidarComResultadoDasFilas(resultado)

        if (this.totalDeRelatoriosEmAndamento > 0) {
          this.timer = setTimeout(this.polling, this.intervaloDeTimeout) // chamada recursiva com timeout
        }
      } else {
        clearTimeout(this.timer)
      }
    },
/**
 * Função que realiza o polling celery para verificar o status de um relatório.
 *
 * @function pollingCelery
 * @param {Object} relatorioInfo - Objeto contendo as informações do relatório para o polling.
 * @param {string} relatorioInfo.taskId - Id do relatório que será gerado.
 * @param {string} relatorioInfo.url - Path da url utilizada para obter o status do relatório.
 * @param {string} relatorioInfo.tipo - O Tipo é utilizado apenas para mostrar em tela `tipo - status`.
 * @returns {Promise<string>} Retorna uma Promise que trata cada relatório individualmente.
 * @throws {Error} Lança um erro (400 ou 500) se a requisição falhar ou se o relatório não for gerado corretamente.
 *
 * @description
 * A função realiza uma requisição para obter o status de um relatório utilizando o `taskId` e a `url` do objeto `relatorioInfo`.
 * Se o relatório for gerado, a função retorna o `relatorio_id`.
 * Caso contrário, atualiza o status do relatório e dispara timeout recursivo após 5 segundos.
 * Atualmente a função é chamada até a obtenção do erro ou sucesso.
 */
    async pollingCelery(relatorioInfo) {
      if(!relatorioInfo.taskId) return;

      const { taskId, url } = relatorioInfo;

      if (!this.relatorioLista[taskId]) {
        this.relatorioLista[taskId] = { ...relatorioInfo };
      }

      const removerRelatorioDaLista = () => {
        const relatorioLista = { ...this.relatorioLista }
        delete relatorioLista[taskId]
        this.relatorioLista = relatorioLista
      }

      return new Promise(async (resolve, reject) => {
        try {
          const resultadoRelatorio = await this.obterRelatorioPorId(url, taskId);
          const { relatorio_id, status } = resultadoRelatorio.data;

          if (relatorio_id) {
            removerRelatorioDaLista()
            this.relatorioInfo = {};
            this.buscarNotificacoes();
            resolve(relatorio_id);
          } else {
            this.relatorioLista[taskId] = { ...relatorioInfo, status: status };
            this.instanciaTimeoutCelery = setTimeout(() => {
              this.pollingCelery(relatorioInfo).then(resolve).catch(reject);
            }, 5000);
          }
        } catch (error) {
          removerRelatorioDaLista()
          this.relatorioInfo = {};

          if(error instanceof CanceledError){
            return;
          }

          this.$modalDeErro(error.response.data.error ? error.response.data.error : this.$t('mainHeader.Notification.erroAoGerarRelatorioGenerico'))
          reject(error);
        }
      });
    },
    buscarNotificacoes(relatorioInfo) {
      // Até o momento, exclusivo para Solar: múltiplos R01
      // Múltiplos relatórios
      if(Array.isArray(relatorioInfo)){
        relatorioInfo.forEach((relatorio) => {
          if (!relatorio?.taskId) {
            return;
          }

          this.relatorioInfo = relatorio;

          if(this.relatorioLista[relatorio?.taskId]){
            return;
          }

          this.pollingCelery(this.relatorioInfo);
        })
      } else {
        // Essa condição garante que a referência de 'relatorioInfo' não seja sobrescrita por PointerEvent
        // Para reproduzir, adicione um console antes da condição e abra o sininho em tela
        if (relatorioInfo?.taskId) {
          this.relatorioInfo = relatorioInfo;
        }

        if(this.relatorioLista[relatorioInfo?.taskId]){
          return;
        }

        this.pollingCelery(this.relatorioInfo);
      }

      this.notificacoesNaoLidas = 0;
      clearTimeout(this.timer);
      this.alterarStatusDeAndamentoDeTodosOsRelatorios(false);
      this.polling();
    },
    async obterRelatorioPorId(url, task_id) {
      return new Promise((resolve, reject) => {
        this.$servicoHttpCondominio.get(url, { params: { task_id }, signal: this.controladorRequisicao.signal })
          .then(resposta => {
            resolve(resposta)
          }).catch(erro => {
            reject(erro)
          });
      });
    },
    async buscarFilaDeRelatoriosAntiga () {
      const observarResposta = this.prepararObservacaoImportacoes()
      return new Promise((resolve, reject) => {
        this.$API.reports.get()
          .then(resposta => {
            observarResposta(resposta.body.success)
            resolve(resposta)
          }).catch(erro => {
            reject(erro)
          })
      })
    },
    async buscarFilaDeRelatorioTravamento () {
      return new Promise((resolve, reject) => {
        if (!this.possuiPermissaoDeVerRelatorio) {
          this.$API.filaRelatorioTravamento.get()
            .then(resposta => {
              resolve(resposta)
            })
            .catch(erro => {
              reject(erro)
            })
        } else {
          this.relatorioFilaTravamentoEstaPronto = true
          resolve()
        }
      })
    },
    async buscarNovaFilaDeRelatorios () {
      const valoresParaConsulta = {
        intervalo_de_horas: 6,
        status_para_retornar: ['NAO_INICIADO', 'GERANDO', 'CONCLUIDO']
      }

      return new Promise((resolve, reject) => {
        this.$API.consultarFilaDeRelatorios.get(valoresParaConsulta)
          .then(resposta => {
            resolve(resposta)
          })
          .catch(erro => {
            reject(erro)
          })
      })
    },
    async buscarFilaDeAcompanhamentoIntegracao () {
      const valoresIntegracao = {
        intervalo: 6
      }
      return new Promise((resolve, reject) => {
        if (this.possuiFlagIntegracaoAtiva) {
          this.$API.acompanhamentoIntegracao.get(valoresIntegracao)
            .then(resposta => {
              resolve(resposta)
            })
            .catch(erro => {
              reject(erro)
            })
        } else {
          this.relatorioAcompanhamentoIntegracaoEstaPronto = true
          resolve()
        }
      })
    },
    lidarComResultadoDasFilas (resultados) {
      // cada posição do array abaixo representa a ordem das requisições
      // feitas no allSettled na função de polling
      resultados.forEach((resultado) => {
        const { status, value: resultadoDeSucesso, reason: erro } = resultado

        switch (status) {
          case 'fulfilled':
            if (resultado === resultados[0]) {
              this.tratarRespostaFilaAntiga(resultadoDeSucesso)
            } else if (resultado === resultados[1]) {
              this.tratarRespostaFilaTravamento(resultadoDeSucesso)
            } else if (resultado === resultados[2]) {
              this.tratarRespostaDaNovaFila(resultadoDeSucesso)
            } else if (resultado === resultados[3]) {
              this.tratarRespostaDoAcompanhamentoDeIntegracao(resultadoDeSucesso)
            }
            break
          case 'rejected':
            if (resultado === resultados[0]) {
              this.tratarErroDaFilaGenerico(erro, 'relatorioFilaAntigaEstaPronto')
            } else if (resultado === resultados[1]) {
              this.tratarErroDaFilaGenerico(erro, 'relatorioFilaTravamentoEstaPronto')
            } else if (resultado === resultados[2]) {
              this.tratarErroDaFilaGenerico(erro, 'relatorioNovaFilaEstaPronto')
            } else if (resultado === resultados[3]) {
              this.tratarErroDaFilaGenerico(erro, 'relatorioAcompanhamentoIntegracaoEstaPronto')
            }
            break
        }
      })
    },
    tratarErroDaFilaGenerico (erro, variavelDeStatus) {
      // caso o erro seja 403, significa que o usuário não possui permissão para ver a fila
      // sendo assim, a fila é considerada pronta
      const ehErro403 = erro?.status === 403
      if (ehErro403 || this.totalDeRelatoriosEmAndamento === 0) {
        this[variavelDeStatus] = true
      }
    },
    tratarRespostaFilaTravamento (response) {
      // Fila apenas para relatório de travamentos
      // Essa rota é livre de permissoes, sendo assim, só é chamanda quando
      // o usuário não possui as permissoes gerais de relatórios

      if (this.possuiPermissaoDeVerRelatorio) {
        return
      }

      const { data, ready, error, info } = response.body.success

      this.relatorioFilaTravamentoEstaPronto = ready

      this.relatoriosFilaTravamentoEmEndamento = this.obterRelatoriosEmAndamento(data)
      this.totalDeRelatoriosFilaTravamentoEmEndamento = this.relatoriosFilaTravamentoEmEndamento.length

      this.relatorioFilaTravamentoConcluidos = this.obterRelatoriosConcluidos(data)
      if (!this.primeiraVezConsultaFilaTravamento) this.verificarRelatoriosDaFila(this.relatorioFilaTravamentoConcluidos, this.filaDeRelatoriosTravamento)

      this.lidarComErrosEInfos(error, info)

      this.filaDeRelatoriosTravamento = data
      this.primeiraVezConsultaFilaTravamento = false
    },
    tratarRespostaFilaAntiga (response) {
      const { data, ready, error, info } = response.body.success
      this.relatorioFilaAntigaEstaPronto = ready

      this.relatoriosAntigosEmAndamento = this.obterRelatoriosEmAndamento(data)
      this.totalDeRelatoriosAntigosEmAndamento = this.relatoriosAntigosEmAndamento.length

      this.relatoriosAntigosConcluidos = this.obterRelatoriosConcluidos(data)

      if (!this.primeiraVezQueConsultaFilaAntiga) this.verificarRelatoriosDaFila(this.relatoriosAntigosConcluidos, this.filaDeRelatoriosAntigos)

      this.lidarComErrosEInfos(error, info)

      this.filaDeRelatoriosAntigos = data
      this.primeiraVezQueConsultaFilaAntiga = false
    },
    tratarRespostaDaNovaFila (response) {
      const { data, ready, error, info } = response.body.success

      this.relatorioNovaFilaEstaPronto = ready
      // separa relatorios por status
      this.relatoriosEmAndamento = this.obterRelatoriosEmAndamento(data)
      this.totalDeNovosRelatoriosEmAndamento = this.relatoriosEmAndamento.length

      this.relatoriosConcluidos = this.obterRelatoriosConcluidos(data)

      if (!this.primeiraVezQueConsulta) this.verificarRelatoriosDaFila(this.relatoriosConcluidos, this.filaDeRelatorios)

      this.lidarComErrosEInfos(error, info)

      this.filaDeRelatorios = data
      this.primeiraVezQueConsulta = false
    },
    tratarRespostaDoAcompanhamentoDeIntegracao (response) {
      if (!this.possuiFlagIntegracaoAtiva) {
        return
      }

      const { data, ready, error, info } = response.body.success

      this.relatorioAcompanhamentoIntegracaoEstaPronto = ready
      // separa relatorios por status
      this.relatoriosFilaAcompanhamentoDeIntegracaoEmEndamento = this.obterRelatoriosEmAndamento(data)
      this.totalDeRelatoriosFilaAcompanhamentoDeIntegracaoEmEndamento = this.relatoriosFilaAcompanhamentoDeIntegracaoEmEndamento.length

      this.relatorioFilaAcompanhamentoDeIntegracaoConcluidos = this.obterRelatoriosConcluidos(data)

      if (!this.primeiraVezQueConsultaAcompanhamentoDeIntegracao) this.verificarRelatoriosDaFila(this.relatorioFilaAcompanhamentoDeIntegracaoConcluidos, this.filaDeRelatoriosAcompanhamentoDeIntegracao)

      this.lidarComErrosEInfos(error, info)

      this.filaDeRelatoriosAcompanhamentoDeIntegracao = data
      this.primeiraVezQueConsultaAcompanhamentoDeIntegracao = false
    },
    verificarRelatoriosDaFila (dados, dadosAntigos) {
      const novosRelatorios = differenceBy(dados, dadosAntigos, 'url')
      const possuiMaisDeUmRelatorioNovo = novosRelatorios.filter(relatorio => relatorio.url).length > 1

      if (possuiMaisDeUmRelatorioNovo) {
        this.$uiv_notify({
          type: 'success',
          title: this.$t('mainHeader.Notification.avisoVariosRelatoriosConcluidos.titulo'),
          content: this.$t('mainHeader.Notification.avisoVariosRelatoriosConcluidos.mensagem')
        })
      }

      novosRelatorios.forEach(relatorio => {
        if (!relatorio.url) return

        if (!possuiMaisDeUmRelatorioNovo) {
          this.enviarNotificacoes(relatorio.name, relatorio.url)
        }

        // guarda a url do relatório para não notificar novamente
        this.relatoriosLidos.push(relatorio.url)

        let allowDownload = true

        const now = moment().format('YYYY-MM-DD HH:mm')
        // so faz o download automatico se o ultimo download foi a mais de 1 minuto
        if (this.lastNotification) {
          const lastMoment = moment(this.lastNotification)
          const nowMoment = moment(now)
          const diff = nowMoment.diff(lastMoment)
          const d = moment(diff).format('mm')

          if (d > 1) {
            allowDownload = true
          } else {
            allowDownload = false
          }
        }

        if (relatorio === novosRelatorios[0]) {
          this.$store.commit('notification/setLastNotificationNow')
        }

        if (allowDownload) {
          const link = document.createElement('a')
          link.download = relatorio.name
          link.href = relatorio.url
          link.click()
        }
      })
    },
    enviarNotificacoes (nomeRelatorio, urlRelatorio) {
      const relatorioFoiLido = this.relatoriosLidos.includes(urlRelatorio)
      if (relatorioFoiLido) return

      if (Notification.permission !== 'granted') {
        Notification.requestPermission()
      } else {
        const notification = new Notification(this.$t('mainHeader.Notification.notificacaoNavegador.titulo'), {
          notificationId: 'avoidmultiple',
          tag: 1,
          icon: '/favicon.png',
          body: this.$t('mainHeader.Notification.notificacaoNavegador.mensagem', [nomeRelatorio])
        })

        notification.onclick = function () {
          window.open(urlRelatorio)
        }
      }

      this.notificacoesNaoLidas += 1

      this.$uiv_notify({
        type: 'success',
        title: nomeRelatorio
      })
    },
    async cancelarRelatorio (relatorioId) {
      try {
        await this.$API.cancelarGeracaoDeRelatorio.save({ ids_relatorios: [relatorioId] })

        this.$swal({
          type: 'success',
          title: this.$t('mainHeader.Notification.avisoRelatorioCancelado')
        })
      } catch (erro) {
        this.$filtradorDeErros.capturarErro(erro)
      }
    },
    obterIdsDeRelatoriosQueEstaoSendoGerados () {
      let relatoriosDaNovaFilaParaCancelar = []

      this.listaDeRelatoriosEmAndamento.map(relatorio => {
        if (relatorio.status === 'GERANDO') {
          relatoriosDaNovaFilaParaCancelar.push(relatorio.id)
        }
      })

      return relatoriosDaNovaFilaParaCancelar
    },
    obterRelatoriosConcluidos (relatorios) {
      let relatoriosConcluidos = []

      relatorios.map(relatorio => {
        if (relatorio.status === 'CONCLUIDO' || relatorio.status === 'SUCCESS') {
          relatoriosConcluidos.push(relatorio)
        } else if (relatorio.url) {
          relatoriosConcluidos.push(relatorio)
        }
      })

      return relatoriosConcluidos
    },
    obterRelatoriosEmAndamento (relatorios) {
      let relatoriosEmAndamento = []

      relatorios.map(relatorio => {
        let relatorioComStatusEmAndamento = relatorio.status === 'NAO_INICIADO' || relatorio.status === 'GERANDO' || relatorio.status === 'PENDING' || relatorio.status === 'STARTED'
        let relatorioPossuiUrl = relatorio.url

        if (relatorioComStatusEmAndamento && !relatorioPossuiUrl) {
          relatoriosEmAndamento.push(relatorio)
        }
      })

      return relatoriosEmAndamento
    },
    lidarComErrosEInfos (erros, info) {
      if (erros) {
        erros.forEach(erro => {
          const notificacaoJaEnviada = this.errosEInfosJaEnviadas.includes(erro?.name || erro?.error)
          if (notificacaoJaEnviada) return

          this.$filtradorDeErros.capturarErro(erro)

          const mensagemDeErro = erro.error
            ? this.$t('mainHeader.Notification.erroAoGerar', [erro.error])
            : this.$t('mainHeader.Notification.erroAoGerarGenerico')

          this.$uiv_notify({
            type: 'danger',
            title: erro.name,
            content: mensagemDeErro
          })
          // identificador para não notificar o erro novamente enquanto ocorrer o polling
          // foi usado o "name" pois o id do relatório com erro pode alterar toda vez que é feita uma chamada na api
          // uma solução é adicionar um id fixo para o relatório no back e depois mudar aqui
          this.errosEInfosJaEnviadas.push(erro?.name || erro?.error)
        })
      }

      if (info) {
        const notificaoJaEnviada = this.errosEInfosJaEnviadas.includes(info)
        if (notificaoJaEnviada) return
        this.$uiv_notify({
          type: 'info',
          content: info
        })

        this.errosEInfosJaEnviadas.push(info)
      }
    },
    alterarStatusDeAndamentoDeTodosOsRelatorios (status) {
      this.relatorioFilaTravamentoEstaPronto = Boolean(status)
      this.relatorioFilaAntigaEstaPronto = Boolean(status)
      this.relatorioNovaFilaEstaPronto = Boolean(status)
      this.relatorioAcompanhamentoIntegracaoEstaPronto = Boolean(status)
    },
    alterarVisibilidadeDropdown() {
      this.dropdownEstaVisivel = !this.dropdownEstaVisivel
    },
  },
  watch: {
    relatorioLista: {
      handler(novoObjeto) {
        localStorage.setItem(this.chaveLocalStorage, JSON.stringify(novoObjeto));
        this.removerChaveVaziaRelatoriosPendentes();
      },
    },
    totalDeRelatoriosEmAndamento: {
      handler(novaQuantidade) {
        this.$emit('update:badgeNotificacoes', novaQuantidade);
      },
      immediate: true
    },
    quantidadeRelatoriosEmAndamento: {
      handler(quantidadeRelatoriosEmAndamento) {
        this.$emit('update:quantidadeRelatoriosEmAndamento', quantidadeRelatoriosEmAndamento);
      },
      immediate: true
    },
    modalEstaAberto: {
      handler(visibilidade) {
        this.$emit('update:visibilidadeNotificacoes', visibilidade);
      },
      immediate: true
    }
  },
  beforeUnmount () {
    // Cancel pending timeouts
    offCheckUserFiles(this.checkUserFilesHandler)
    clearTimeout(this.timer)
    clearTimeout(this.instanciaTimeoutCelery)
    this.controladorRequisicao.abort()
  }
}
</script>

<style scoped>
p {
  margin: 0;
}

pontotel-menu-vertical-gaveta-lateral,
.gaveta,
.gaveta p,
.gaveta span {
  color: var(--menu-vertical-cor-gaveta-lateral-texto-principal);
}

.notification {
  list-style: none;
}

.notification:not(.notification--controlada-externamente) {
  align-items: center;
  display: grid;
  height: 100%;
  justify-items: center;
  width: 100%;
}

.main-header__button--category {
  color: var(--front-color-font-header);
  text-overflow: ellipsis;
  text-shadow: none;
  white-space: nowrap;
  font-weight: 300;
  font-size: 1em;
  padding: 0 .5rem;
  display: flex;
}

.arrow-img {
  position: relative;
  top: 0px;
  left: 5px;
  font-size: 12px;
  font-weight: bold;
}

.badge {
  display: inline-block;
  min-width: 10px;
  padding: 1px 7px;
  font-size: 12px;
  font-weight: normal;
  color: var(--front-color-header);
  line-height: 1;
  vertical-align: baseline;
  white-space: nowrap;
  text-align: center;
  background-color: var(--front-color-font-header);
  border-radius: 10px;
  margin-left: 5px;
}

.icon-spin {
  animation-name: spin;
  animation-duration: 800ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.gaveta {
  display: flex;
  flex-direction: column;
  gap: 35px;
}

.gaveta__secao {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.gaveta__secao--sucesso {
  gap: 10px;
}

.relatorio {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.relatorio__cabecalho {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 1 auto;
  gap: 5px;
  min-width: 0;
}

.relatorio--concluido {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
  min-height: 29px;
}

.relatorio__nome {
  max-width: 100%;
  min-width: 0;
  word-break: break-word;
  /* 11px (pp) era pequeno demais p/ leitura; sobe p/ 13px (acessibilidade) */
  font-size: 13px;
}

.relatorio__progresso {
  color: var(--menu-vertical-cor-gaveta-lateral-texto-secundario) !important;
  margin-top: 2px;
  font-size: 13px;
}

.relatorio--em-andamento .relatorio__nome {
  flex: 1;
  width: 100%;
}

.relatorio__acao-tooltip {
  flex: 0 0 auto;
}

.relatorio__tooltip-indisponivel {
  flex: 1 1 auto;
  min-width: 0;
}

.relatorio--em-andamento .relatorio__acao-tooltip--cancelar {
  display: none;
}

.relatorio--em-andamento.relatorio--pode-cancelar:hover .relatorio__nome,
.relatorio--concluido:hover .relatorio__nome {
  display: block;
  flex: 1 1 auto;
  max-width: 100%;
  min-width: 0;
  white-space: nowrap;
  word-break: normal;
  overflow: hidden;
  text-overflow: ellipsis;
}

.relatorio:hover .relatorio__acao-tooltip--cancelar {
  display: inline-flex;
}

.relatorio--concluido .relatorio__nome {
  flex: 1;
}

.relatorio--concluido .relatorio__acao-tooltip--download {
  display: none;
}

.relatorio--concluido:hover .relatorio__acao-tooltip--download {
  display: inline-flex;
}
</style>
